import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, SimpleChange, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { OverflowMenuComponent } from '@app/core/components/overflow-menu/overflow-menu.component';
import { GridComponent } from '../../grid.component';
import { ColumnLayoutSchema, GridFilterSetting, GridOverflowButton, GridToolbar, GridToolbarButton, GridUserSettings, GridToggleButton, TTGridState, GridRow } from '../../grid.types';
import { exportToPDF } from '../../grid-pdf-export';
import { StateService } from '@app/core/services/state.service';
import { Subscription } from 'rxjs';
import { FilterModel } from '@ag-grid-community/core';
import { GridService } from '../../grid.service';
import { GridModalService } from '../../modals/grid-modal.service';
import { userSettingsToGridState } from '../../helpers/grid.helpers';
import { groupGridButtonsByOverflow, sortGridButtonsByOrderBy } from '../../helpers/grid-button.helpers';

@Component({
    selector: 'tt-grid-toolbar',
    templateUrl: './grid-toolbar.component.html',
    styleUrls: ['./grid-toolbar.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class GridToolbarComponent<TType extends GridRow = GridRow> implements AfterViewChecked, OnChanges, OnDestroy {
    @Input()
    public ttProjectedMenus?: QueryList<OverflowMenuComponent>;

    private destroyed = false;

    @Input()
    public grid?: GridComponent<TType>;

    @Input()
    public ttColschemaToolbar: GridToolbar | undefined;
    private colschemaToolbar?: GridToolbar;

    @Input()
    public ttButtons: GridToolbarButton[] | undefined = [];

    @Input()
    public ttToggles: GridToggleButton[] | undefined = [];
    public toggles: GridToggleButton[] = [];

    /**
     * Whether field names are showing or not.
     */
    private showFieldNames: boolean = false;

    public hasActiveFilters = false;

    public filterChangeSubscription?: Subscription;

    public refreshToolbarSubscription?: Subscription;

    public buttons: (GridToolbarButton | GridOverflowButton<GridToolbarButton>)[] = [];

    constructor(
        public elementRef: ElementRef,
        private state: StateService,
        private gridService: GridService,
        private gridModalService: GridModalService,
    ) {}

    public onClick(button: GridToolbarButton | GridOverflowButton<GridToolbarButton>, event: MouseEvent) {
        if (button.type === 'overflow') return;

        if (button.name.startsWith('§print')) {
            event.preventDefault();
            this.grid?.openPrintDialog(button);
        } else if (button.func) {
            event.preventDefault();
            button.func(event);
        }
    }

    public exportToExcel(event?: MouseEvent | KeyboardEvent) {
        if ((!!event && 'pointerType' in event && event.pointerType === 'mouse') || document.activeElement?.classList.contains('tt-grid__toolbar-button'))
            this.grid?.gridApi?.exportDataAsExcel({
                columnKeys: this.getColumnKeysToBeExportedToExcel(),
                processCellCallback: (params) => {
                    if (!!params.value && params.value instanceof Date) {
                        return new Date(params.value.getTime() - params.value.getTimezoneOffset() * 60000).toISOString();
                    }

                    return params.value;
                },
                processHeaderCallback: (params) => {
                    if (this.grid?.options.excelExportConfig?.useColnameForHeaders === true) {
                        return params.column.getColId();
                    } else {
                        return params.column.getColDef().headerName || '';
                    }
                },
            });
    }

    public exportToPdf(event?: MouseEvent) {
        event?.preventDefault();

        if (this.grid?.gridApi && this.grid?.agGrid) {
            exportToPDF(this.grid.gridApi, `${this.state.getCurrentName()}_grid.pdf`);
        }
    }

    public toggleFloatingFilters(event?: MouseEvent) {
        event?.preventDefault();

        if (!this.grid) return;

        if (this.grid.defaultColumnDefinitions.floatingFilter === true) this.grid.gridApi?.setFilterModel(null);

        this.grid.updateDefaultColumnDefinition({ floatingFilter: !this.grid.defaultColumnDefinitions.floatingFilter });
    }

    public clearAllFilters(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.gridApi?.setFilterModel(null);
    }

    public toggleFieldNameAndTitle(event?: MouseEvent) {
        event?.preventDefault();

        if (!this.grid) return;

        const columnState = this.grid.beforeColumnStateUpdate();

        this.showFieldNames = !this.showFieldNames;

        this.grid.options.data!.columnDefinitions = this.grid.options.data!.columnDefinitions.map((column) => {
            if (!!column.field && this.grid!.options.data?.columnInfo?.[column.field]) {
                const title = this.showFieldNames ? this.grid!.options.data?.columnInfo?.[column.field!].colname : this.grid!.options.data?.columnInfo?.[column.field!].title || '';

                return { ...column, headerName: title };
            } else {
                return column;
            }
        });

        this.grid?.afterColumnStateUpdate(columnState);
    }

    public refreshData(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.options.gridfunc?.refresh();
    }

    public readData(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.options.gridfunc?.read();
    }

    public rebindGrid(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.options.gridfunc?.rebind();
    }

    public addRowBefore(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.addRow({ addBefore: true });
    }

    public addRowAfter(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.addRow({ addBefore: false });
    }

    public removeSelectedRows(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.options.gridfunc?.removeRows();
    }

    public saveAllChanges(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.options.gridfunc?.saveChanges();
    }

    public toggleTextWrapping(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.updateDefaultColumnDefinition({ wrapText: !this.grid.defaultColumnDefinitions.wrapText, wrapHeaderText: !this.grid.defaultColumnDefinitions.wrapText });
    }

    public autoSizeAllColumns(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.gridApi?.autoSizeAllColumns();
    }

    public async openColumnLayoutDialog(event?: MouseEvent) {
        event?.preventDefault();

        if (!this.grid) return;

        if (this.grid!.options.dataTask?.loadData?.method && !!this.grid!.gridApi) {
            const result = await this.gridModalService.openColumnLayoutDialog<TType>(this.grid.options, this.getGridStateForColumnLayout());

            if (result) {
                if ('errormessage' in result) {
                    this.grid.options.data!.layoutKeyno = result.gridlayout_keyno;
                    this.grid.rememberGridState$.next({ ...this.grid.gridApi.getState(), gridlayout_keyno: this.grid.options.data?.layoutKeyno } as TTGridState);
                } else {
                    const layout = JSON.parse(this.gridService.atou(result.layout_schema));

                    this.grid.options.data!.layoutKeyno = result.gridlayout_keyno;

                    this.applyColumnLayoutSchema(layout);
                }
            }
        }
    }

    public openPrintDialog(event?: MouseEvent) {
        event?.preventDefault();
        this.grid?.openPrintDialog();
    }

    ngAfterViewChecked(): void {
        const toolbarClasses = ['ag-button', 'ag-floating-filter-button-button', 'tt-grid__toolbar-button'];

        const menus = this.ttProjectedMenus?.filter((menu) => {
            const trigger = document.getElementById(menu.id.button);

            if (!trigger || trigger.closest('tt-grid-toolbar') !== this.elementRef.nativeElement) return false;
            if (!trigger.closest('tt-overflow-menu')?.hasAttribute('toolbarButtons')) return false;

            const classes = menu.ttClasses.split(/\s+/);

            return toolbarClasses.some((className) => !classes.includes(className));
        });

        if (!menus?.length) return;

        // Defer input updates until the projected views have finished their current check.
        void Promise.resolve().then(() => {
            if (this.destroyed) return;

            menus.forEach((menu) => {
                const classes = new Set([...menu.ttClasses.split(/\s+/).filter(Boolean), ...toolbarClasses]);

                menu.ttClasses = [...classes].join(' ');
                menu.ttIcon ||= 'fal fa-ellipsis-v';
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['grid']) {
            if (!!this.filterChangeSubscription) {
                this.filterChangeSubscription.unsubscribe();
            }

            if (!!this.refreshToolbarSubscription) {
                this.refreshToolbarSubscription.unsubscribe();
            }

            this.setHasActiveFilters(this.grid?.gridApi?.getFilterModel() ?? {});
            this.setToolbarVisibility();

            this.filterChangeSubscription = this.grid?.onFilterChange.subscribe({ next: this.setHasActiveFilters });
            this.refreshToolbarSubscription = this.grid?.refreshToolbar.subscribe({ next: this.setToolbarVisibility });
        }

        if (this.isValidButtonsChanges(changes['ttColschemaToolbar'])) {
            this.colschemaToolbar = this.ttColschemaToolbar;
        }

        if (this.isValidButtonsChanges(changes['ttButtons']) || this.isValidButtonsChanges(changes['ttColschemaToolbar'])) {
            this.setButtons();
        }

        if (this.isValidButtonsChanges(changes['ttToggles'])) {
            this.toggles = changes['ttToggles'].currentValue;
        }
    }

    private setButtons() {
        this.buttons = [];

        this.addButtonsFrom(this.ttButtons ?? []);
        this.setPredifinedButtonsVisibilityFromColschema();
        this.addButtonsFrom(sortGridButtonsByOrderBy(this.colschemaToolbar?.buttons ?? []));

        this.buttons = groupGridButtonsByOverflow(this.buttons as GridToolbarButton[]);

        if (this.buttons) this.setToolbarVisibility();
    }

    private setPredifinedButtonsVisibilityFromColschema() {
        if (!this.grid) return;

        for (let k in this.colschemaToolbar) {
            let key: keyof GridToolbar = k as keyof GridToolbar;

            if (key !== 'buttons' && key !== 'toggles' && typeof this.colschemaToolbar[key] === 'boolean') {
                this.grid.options!.config!.toolbar![key] = this.colschemaToolbar[key];
            }
        }
    }

    private addButtonsFrom(buttons: GridToolbarButton[]) {
        for (const button of buttons) {
            if (button.type === 'print') {
                this.grid!.options.config!.toolbar!.print = true;
            } else if (!this.buttons.some((b) => b.name === button.name)) {
                this.buttons.push(button);
            }
        }
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.filterChangeSubscription?.unsubscribe();
    }

    private getColumnKeysToBeExportedToExcel() {
        return Array.from(new Set([...(this.grid?.gridApi?.getAllDisplayedColumns() ?? []).map((column) => column.getColId()).filter((colId) => !(this.grid?.options.excelExportConfig?.excludedColumns ?? []).includes(colId)), ...(this.grid?.options.excelExportConfig?.includedColumns ?? [])]));
    }

    private setHasActiveFilters = (value: FilterModel) => {
        this.hasActiveFilters = Object.keys(value).length > 0;
    };

    /**
     * Checks if the change is a valid buttons change, meaning if its not a list the buttons shouldnt update in the component, only if the last has changes.
     *
     * @param change the buttons change to check.
     */
    private isValidButtonsChanges(change: SimpleChange | undefined) {
        return !!change?.currentValue && (Array.isArray(change.currentValue) || (typeof change.currentValue === 'object' && 'buttons' in change.currentValue && Array.isArray(change.currentValue.buttons)));
    }

    private setToolbarVisibility = (refreshFromColschema?: boolean | void) => {
        if (refreshFromColschema === true) {
            this.refreshColschemaButtons();
        }

        this.buttons.forEach(async (button) => {
            if (button.type === 'overflow' && 'buttons' in button) {
                button.buttons.forEach(async (item) => (item._disabled = await this.getDisabledValueForGridToolbarButton(item)));
                button.buttons = [...button.buttons];
            }

            button._disabled = await this.getDisabledValueForGridToolbarButton(button);
        });

        this.grid?.options.config?.toolbar?.toggles?.forEach(async (toggle) => {
            toggle._disabled = await this.getDisabledValueForGridToolbarButton(toggle);
        });
    };

    private async getDisabledValueForGridToolbarButton(button: GridToolbarButton | GridOverflowButton<GridToolbarButton> | GridToggleButton): Promise<boolean | 'hidden'> {
        let result: boolean | 'hidden' = 'hidden';

        try {
            result = (await button.disabled?.()) ?? false;
        } catch (error) {
            console.error(error);
        }

        if (result === 'hidden') return result;

        if (!!button.disable_if_no_selected && ['1', 1, true, 'true'].includes(button.disable_if_no_selected)) {
            try {
                let disabledIfNoSelected = false;

                if (this.grid?.options.data?.columnDefinitions.some((col) => col.colId === 'is_selected')) {
                    disabledIfNoSelected = !!this.grid?.options.gridfunc && (this.grid?.options?.gridfunc?.getIsSelectedRows()?.length ?? 0) > 0 ? false : true;
                } else {
                    disabledIfNoSelected = !!this.grid?.options.gridfunc && !!this.grid.gridApi && (this.grid?.options.gridfunc?.getSelectedRows()?.length ?? 0) > 0 ? false : true;
                }

                result = disabledIfNoSelected;
            } catch (error) {
                console.error(error);
                result = true;
            }
        }

        return result;
    }

    private async refreshColschemaButtons() {
        if (!this.grid?.options.dataTask?.loadData) return;

        const parameters = await this.gridService.getLoadDataParameters(this.grid.options.dataTask.loadData.parameters);
        const columns = await this.gridService.getGridColumns({ method: !!this.grid.options.dataTask.loadSetupId ? +this.grid.options.dataTask.loadSetupId : 1999, loadDataMethod: Number(this.grid.options.dataTask.loadData.method), params: parameters, force: true });
        const { gridOptions } = this.gridService.getDBGridOptions(columns);

        this.colschemaToolbar = gridOptions?.toolbar;
        this.setButtons();
    }

    /**
     * Applies the given column layout schema to the usersettings and persists the changes.
     *
     * @param schema the schema to apply.
     */
    private applyColumnLayoutSchema(schema: ColumnLayoutSchema | TTGridState) {
        if (!this.grid) return;

        let state: TTGridState;

        if (schema.agGrid === true) {
            state = schema as TTGridState;
        } else {
            schema = schema as ColumnLayoutSchema;
            const filterable = schema?.tt_grid_filterable ?? false;
            delete schema.tt_grid_filterable;
            const columnSettings = schema;
            let userSettings: GridUserSettings;

            if (filterable === true || filterable === false) {
                const filterSettings: GridFilterSetting[] = Object.keys(schema)
                    .map((key) => (!!(schema as ColumnLayoutSchema)?.[key]?.filter ? { ...(schema as ColumnLayoutSchema)?.[key].filter, field: key } : null))
                    .filter((filter) => !!filter) as GridFilterSetting[];
                userSettings = { columns: columnSettings, filter: { filters: filterSettings }, filterable: filterable === true ? { mode: 'row' } : false };
            } else {
                userSettings = { columns: columnSettings, filter: { filters: [] } };
            }

            state = userSettingsToGridState(userSettings, this.grid.options.data?.columnDefinitions);
        }

        if (!!state) {
            state.gridlayout_keyno = this.grid.options.data?.layoutKeyno;
        }

        const columnsToHide = this.grid.options.data?.columnDefinitions.map((colDef) => colDef.colId!).filter((colId) => !state.columnOrder?.orderedColIds.includes(colId!)) ?? [];

        if (state.columnVisibility) {
            state.columnVisibility.hiddenColIds.push(...columnsToHide);
        }

        if (state.columnOrder) {
            state.columnOrder.orderedColIds.push(...columnsToHide);
        }

        if (state.wrapText != undefined) {
            this.grid?.updateDefaultColumnDefinition({ wrapText: state.wrapText });
        }

        if (state.wrapHeaderText !== undefined) {
            this.grid?.updateDefaultColumnDefinition({ wrapHeaderText: state.wrapHeaderText });
        }

        this.grid?.gridApi?.setGridOption('onGridPreDestroyed', undefined);
        this.grid.columnLayoutChanged = true;
        this.grid.gridStateReady = false;
        this.grid.cdr.markForCheck();

        this.grid.prepareGridStateForInitialization(state);
        this.grid.rememberGridState(state);

        setTimeout(() => {
            this.grid!.setOnGridPreDestroyedRemember();
            this.grid!.gridStateReady = true;
            this.grid!.cdr.markForCheck();
        });
    }

    /**
     * Retrieves a simplified version of the current grid state which can be used as a premade column layout.
     *
     * @returns simplified grid state intended to be used as a column layout.
     */
    private getGridStateForColumnLayout() {
        const state: TTGridState = { ...this.grid!.gridApi!.getState(), agGrid: true, floatingFilter: this.grid!.defaultColumnDefinitions.floatingFilter, gridlayout_keyno: this.grid!.options.data?.layoutKeyno, wrapText: this.grid!.defaultColumnDefinitions.wrapText ?? false, wrapHeaderText: this.grid!.defaultColumnDefinitions.wrapHeaderText ?? false };
        delete state.scroll;
        delete state.rowSelection;
        delete state.rangeSelection;
        delete state.focusedCell;
        delete state.gridlayout_keyno;

        return state;
    }
}
